/** @file Compiles and links the viewport shader; returns the program and its uniform locations. */
import { invariant } from '@pose2d/std/invariant';
import { SHADER_SOURCE } from './shaderSource.data';

/** The linked program plus the uniform locations a frame sets. */
export interface SpriteProgram {
  program: WebGLProgram;
  row0: WebGLUniformLocation | null;
  row1: WebGLUniformLocation | null;
  page: WebGLUniformLocation | null;
}

/** Compiles one stage; on failure deletes it and throws with the GL info log. */
function compileStage(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  invariant(shader, 'createShader returned null (context lost?)');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  const ok = gl.getShaderParameter(shader, gl.COMPILE_STATUS) === true;
  const log = ok ? '' : (gl.getShaderInfoLog(shader) ?? '');
  if (!ok) gl.deleteShader(shader);
  invariant(ok, `shader compile failed: ${log}`);
  return shader;
}

/** Compiles both stages, links them, uses the program with uPage = texture unit 0; throws with the GL log. */
export function compileSpriteProgram(gl: WebGL2RenderingContext): SpriteProgram {
  const vertex = compileStage(gl, gl.VERTEX_SHADER, SHADER_SOURCE.vertex);
  const fragment = compileStage(gl, gl.FRAGMENT_SHADER, SHADER_SOURCE.fragment);
  const program = gl.createProgram();
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  // Flagged for deletion; they live while attached and go with the program.
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  const linked = gl.getProgramParameter(program, gl.LINK_STATUS) === true;
  const log = linked ? '' : (gl.getProgramInfoLog(program) ?? '');
  if (!linked) gl.deleteProgram(program);
  invariant(linked, `shader link failed: ${log}`);
  gl.useProgram(program);
  const page = gl.getUniformLocation(program, 'uPage');
  gl.uniform1i(page, 0);
  return { program, row0: gl.getUniformLocation(program, 'uRow0'), row1: gl.getUniformLocation(program, 'uRow1'), page };
}
